/**
 * DBN Executive Overview — Correct Information Hierarchy
 * Section 1: Headline KPIs → Section 2: Health Scores →
 * Section 3: Capital Flow + Facilities → Section 4: Sector | Map | Actions →
 * Section 5: AI Insights → Section 6: Peer Benchmark (collapsed)
 */
import React, { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, Legend } from "recharts";
import { dbnApi, KPICard, HealthCard, SectionHeader, ProgressBar, Badge,
  PageHeader, LoadingState, fmtEur, fmtN, fmtPct, Btn } from "./dbnUtils";
import DBNNigeriaMap from "./DBNNigeriaMap";
import {
  DollarSign, Users, Briefcase, TrendingUp, Leaf, Zap,
  AlertTriangle, Target, ChevronRight, ChevronDown, Check
} from "lucide-react";
import client from "@/api/client";

function AlertCard({ alert, onResolve }) {
  const colors = { High:"#ef4444", Medium:"#f59e0b", Low:"#60a5fa" };
  const c = colors[alert.severity] || "#60a5fa";
  return (
    <div style={{
      background:"#060f1e", borderRadius:10, padding:"10px 12px",
      borderLeft:`3px solid ${c}`, marginBottom:7
    }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4 }}>
        <div style={{ display:"flex", gap:5 }}>
          <Badge label={alert.severity} color={c} />
          <Badge label={alert.type} color="#334155" />
        </div>
        <button onClick={() => onResolve(alert.id)}
          style={{ fontSize:8, color:"#475569", background:"none", border:"none", cursor:"pointer",
            padding:"1px 6px", borderRadius:4, border:"1px solid #1e3a5f" }}>
          Resolve
        </button>
      </div>
      <p style={{ fontSize:9.5, color:"#94a3b8", lineHeight:1.5, marginBottom:3 }}>{alert.message}</p>
    </div>
  );
}

function CapitalFlowWaterfall({ stages, facilitiesSummary }) {
  const [expanded, setExpanded] = useState(null);
  const colors = ["#34d399","#60a5fa","#a78bfa","#fbbf24","#f97316"];
  return (
    <div>
      <div style={{ display:"flex", alignItems:"stretch", gap:0 }}>
        {(stages || []).map((stage, i) => (
          <React.Fragment key={i}>
            <div
              onClick={() => setExpanded(expanded===i?null:i)}
              style={{
                flex:1, textAlign:"center", padding:"10px 4px", cursor:"pointer",
                background:expanded===i?"rgba(52,211,153,0.06)":"transparent",
                borderRadius:7, transition:"background .15s", minWidth:72
              }}>
              <p style={{ fontSize:8.5, color:"#475569", textTransform:"uppercase",
                letterSpacing:"0.07em", marginBottom:3 }}>{stage.label}</p>
              <p style={{ fontSize:stage.currency==="JOBS"?11:13, fontWeight:900,
                color:colors[i]||"#f0f9ff", lineHeight:1 }}>
                {stage.currency==="JOBS" ? `${fmtN(stage.value)} jobs` : fmtEur(stage.value)}
              </p>
              {stage.pct != null && (
                <p style={{ fontSize:7.5, color:"#334155", marginTop:2 }}>{stage.pct}% utilised</p>
              )}
            </div>
            {i < (stages||[]).length-1 && (
              <div style={{ display:"flex", alignItems:"center", padding:"0 1px" }}>
                <ChevronRight size={11} color="#1e3a5f" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      {expanded === 2 && (
        <div style={{ background:"#060f1e", borderRadius:8, padding:"8px 12px", marginTop:8 }}>
          <p style={{ fontSize:9, color:"#475569", marginBottom:6, fontWeight:700 }}>PFI Allocation Breakdown</p>
          {(facilitiesSummary||[]).map(f => (
            <div key={f.id} style={{ marginBottom:6 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
                <span style={{ fontSize:9, color:"#94a3b8" }}>{f.partner}</span>
                <span style={{ fontSize:9, color:"#64748b" }}>{f.pct}%</span>
              </div>
              <ProgressBar value={f.pct} color="#34d399" height={4} showPct={false} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const SECTOR_COLORS = {
  "Renewable Energy":"#34d399","Agriculture":"#60a5fa","Clean Transportation":"#a78bfa",
  "Circular Economy":"#fbbf24","Green Manufacturing":"#f97316","Water and Sanitation":"#0891b2",
  "Healthcare":"#ec4899","SME Recovery":"#6b7280","Clean Energy":"#0d9488",
};

export default function DBNOverview() {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [mapData, setMapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [colorBy, setColorBy] = useState("type");
  const [alerts, setAlerts] = useState([]);
  const [benchmarks, setBenchmarks] = useState(null);
  const [showBenchmarks, setShowBenchmarks] = useState(false);
  const [showAllAlerts, setShowAllAlerts] = useState(false);

  const loadData = () => {
    Promise.all([dbnApi.overview(), dbnApi.portfolioHealth()])
      .then(([r1, r2]) => {
        setData(r1.data);
        setHealth(r2.data);
        setAlerts(r1.data?.alerts || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    client.get("/dbn/map-data").then(r => setMapData(r.data?.features||[])).catch(()=>{});
  };

  useEffect(() => { loadData(); }, []);

  const handleResolve = async (id) => {
    await dbnApi.resolveAlert(id);
    setAlerts(prev => prev.filter(a => a.id !== id));
  };

  if (loading) return <LoadingState />;
  if (!data) return null;
  const k = data.kpis || {};
  const ph = health?.scores || {};

  const sectorData = (data.sector_allocation||[]).map(s => ({
    name: s.sector.split(" ")[0],
    fullName: s.sector,
    value: s.value,
    fill: SECTOR_COLORS[s.sector] || "#475569"
  })).slice(0,7);

  const displayAlerts = showAllAlerts ? alerts : alerts.slice(0,3);

  return (
    <div style={{ display:"flex", flexDirection:"column", minHeight:"100%" }}>
      <PageHeader
        title="DBN Green, Digital and Inclusive Finance Portfolio"
        subtitle="Capital deployment · PFI performance · MSME growth · Development impact">
        <Badge label="Q3 2025" color="#334155" />
        <button
          onClick={() => window.open(dbnApi.reportDownload("portfolio-brief","pdf"))}
          style={{ padding:"5px 14px", borderRadius:8, fontSize:10, fontWeight:700,
            background:"linear-gradient(135deg,#064e3b,#0f766e)", border:"none",
            color:"#34d399", cursor:"pointer" }}>
          Generate Executive Brief
        </button>
      </PageHeader>

      <div style={{ padding:"14px 24px", flex:1 }}>
        {/* Synthetic data notice */}
        <div style={{ background:"#1a0a00", border:"1px solid #78350f20", borderRadius:7,
          padding:"5px 12px", marginBottom:12, display:"flex", alignItems:"center", gap:7 }}>
          <AlertTriangle size={10} color="#f59e0b" />
          <p style={{ fontSize:9, color:"#78350f" }}>
            Demo environment — all institutions, projects, facilities, transactions and performance data are synthetic.
          </p>
        </div>

        {/* ── SECTION 1: Headline KPIs ── */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:10 }}>
          <KPICard label="Total Portfolio" value={fmtEur(k.total_portfolio_eur)}
            sub={`≈ ₦${fmtN(k.total_portfolio_eur*1640)}`} color="#34d399" icon={DollarSign} testId="kpi-portfolio" trend={5.2} />
          <KPICard label="Allocated to PFIs" value={fmtEur(k.allocated)}
            sub={`${fmtPct(k.allocated/k.total_portfolio_eur*100)} of total`} color="#60a5fa" icon={TrendingUp} />
          <KPICard label="Disbursed to MSMEs" value={fmtEur(k.disbursed)}
            sub={`${fmtPct(k.disbursed/k.total_portfolio_eur*100)} utilisation`} color="#a78bfa" icon={Zap} trend={3.8} />
          <KPICard label="MSMEs & Mid-Caps" value={fmtN(k.total_msmecap)}
            sub={`${k.active_pfis} active PFIs`} color="#fbbf24" icon={Users} />
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:14 }}>
          <KPICard label="Jobs Supported" value={fmtN(k.jobs_supported)}
            sub="Direct + indirect" color="#f97316" icon={Briefcase} trend={4.6} />
          <KPICard label="Private Capital Mobilised" value={fmtEur(k.private_capital_mobilised)}
            sub={`${(k.private_capital_mobilised/k.disbursed).toFixed(1)}x leverage`} color="#34d399" icon={TrendingUp} />
          <KPICard label="Green & Digital Projects" value={k.green_digital_projects}
            sub={`of ${k.total_projects} total projects`} color="#0d9488" icon={Leaf} />
          <KPICard label="Emissions Avoided" value={`${fmtN(k.emissions_avoided_tco2e)} tCO₂e`}
            sub="Estimated · pre-verification" color="#22c55e" icon={Leaf} />
        </div>

        {/* ── SECTION 2: Portfolio Health ── */}
        <div style={{ marginBottom:14 }}>
          <SectionHeader title="Portfolio Health and Performance"
            sub="Composite scores across capital deployment, impact, compliance and pipeline dimensions" />
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
            {Object.entries(ph).map(([key, v]) => (
              <HealthCard key={key} label={v.label} score={v.score} trend={v.trend}
                details={v.details} status={v.status} />
            ))}
          </div>
        </div>

        {/* ── SECTION 3: Capital Flow + Facilities ── */}
        <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:14, marginBottom:14 }}>
          <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:14 }}>
            <SectionHeader title="Capital Deployment Flow"
              sub="Development partner → DBN → PFIs → MSMEs · Click stages to drill down" />
            <CapitalFlowWaterfall stages={data.capital_flow} facilitiesSummary={data.facilities_summary} />
          </div>
          <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:14 }}>
            <SectionHeader title="Active Facilities" sub="4 development partners" />
            <div style={{ display:"flex", flexDirection:"column", gap:9 }}>
              {(data.facilities_summary||[]).map(f => (
                <div key={f.id}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                    <p style={{ fontSize:10, color:"#94a3b8", fontWeight:600 }}>{f.partner}</p>
                    <p style={{ fontSize:10, color:"#64748b" }}>{f.pct}%</p>
                  </div>
                  <ProgressBar value={f.pct} color="#34d399" height={5} showPct={false} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── SECTION 4: Portfolio by Sector | Nigeria Map | Action Required ── */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1.2fr 1fr", gap:14, marginBottom:14 }}>
          {/* Portfolio by Sector */}
          <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:14 }}>
            <SectionHeader title="Portfolio by Sector" sub="Finance amount allocation" />
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={sectorData} layout="vertical" margin={{ top:0, right:10, bottom:0, left:0 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" tick={{ fill:"#475569", fontSize:9 }} width={60} />
                <Tooltip
                  contentStyle={{ background:"#0a1628", border:"1px solid #1e3a5f", borderRadius:8, fontSize:9 }}
                  formatter={(v, n, p) => [fmtEur(p.payload.value*1000), p.payload.fullName]}
                  labelFormatter={() => ""}
                />
                <Bar dataKey="value" radius={[0,4,4,0]}>
                  {sectorData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Nigeria Map */}
          <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:14 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
              <div>
                <p style={{ fontSize:13, fontWeight:800, color:"#f0f9ff" }}>Nigeria Portfolio Map</p>
                <p style={{ fontSize:10, color:"#475569", marginTop:2 }}>{mapData.length} projects · 22 states</p>
              </div>
              <div style={{ display:"flex", gap:2 }}>
                {["type","risk"].map(c => (
                  <button key={c} onClick={() => setColorBy(c)}
                    style={{ padding:"3px 8px", borderRadius:5, fontSize:8, fontWeight:700, border:"none", cursor:"pointer",
                      background:colorBy===c?"#1e3a5f":"#060f1e", color:colorBy===c?"#93c5fd":"#475569" }}>
                    {c === "type" ? "By Type" : "By Risk"}
                  </button>
                ))}
              </div>
            </div>
            <DBNNigeriaMap features={mapData} colorBy={colorBy} height={232} />
          </div>

          {/* Action Required — prominent */}
          <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:14,
            display:"flex", flexDirection:"column" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
              <div>
                <p style={{ fontSize:13, fontWeight:800, color:"#f0f9ff" }}>Action Required</p>
                <p style={{ fontSize:10, color:alerts.length>0?"#ef4444":"#22c55e", marginTop:2 }}>
                  {alerts.length} alert{alerts.length!==1?"s":""} requiring attention
                </p>
              </div>
              {alerts.filter(a=>a.severity==="High").length > 0 && (
                <Badge label={`${alerts.filter(a=>a.severity==="High").length} High`} color="#ef4444" />
              )}
            </div>
            <div style={{ flex:1, overflowY:"auto" }}>
              {alerts.length === 0 ? (
                <div style={{ textAlign:"center", padding:"20px 0" }}>
                  <Check size={22} color="#34d399" style={{ margin:"0 auto 7px", display:"block" }} />
                  <p style={{ fontSize:11, color:"#475569" }}>All alerts resolved</p>
                </div>
              ) : (
                <>
                  {displayAlerts.map(a => (
                    <AlertCard key={a.id} alert={a} onResolve={handleResolve} />
                  ))}
                  {alerts.length > 3 && (
                    <button onClick={() => setShowAllAlerts(!showAllAlerts)}
                      style={{ width:"100%", background:"#060f1e", border:"1px solid #0f2033",
                        borderRadius:8, padding:"6px", fontSize:9, color:"#475569",
                        cursor:"pointer", fontWeight:600, marginTop:4 }}>
                      {showAllAlerts ? `Show Less` : `View All ${alerts.length} Alerts`}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── SECTION 5: AI Portfolio Insights ── */}
        <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:14, marginBottom:14 }}>
          <SectionHeader title="AI Portfolio Insights" sub="Intelligence-driven recommendations · Updated daily" />
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
            {(data.insights||[]).map((ins, i) => {
              const colors = ["#34d399","#f59e0b","#60a5fa"];
              const icons = [TrendingUp, AlertTriangle, Target];
              const Icon = icons[i] || TrendingUp;
              return (
                <div key={i} style={{ background:"#060f1e", borderRadius:10, padding:"12px 14px",
                  borderTop:`3px solid ${colors[i]}` }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:7 }}>
                    <Icon size={12} color={colors[i]} />
                    <p style={{ fontSize:11, fontWeight:700, color:"#f0f9ff" }}>{ins.title}</p>
                  </div>
                  <p style={{ fontSize:10, color:"#64748b", lineHeight:1.6 }}>{ins.body}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── SECTION 6: Illustrative Peer Benchmark (collapsed) ── */}
        <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:14 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:showBenchmarks?12:0 }}>
            <div>
              <p style={{ fontSize:13, fontWeight:800, color:"#f0f9ff" }}>Portfolio Peer Benchmarking</p>
              <p style={{ fontSize:10, color:"#475569", marginTop:2 }}>DBN position vs synthetic peer group · Optional executive view</p>
            </div>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <div style={{ background:"#1a0a00", border:"1px solid #78350f30", borderRadius:6, padding:"3px 9px" }}>
                <p style={{ fontSize:8, color:"#f59e0b", fontWeight:700 }}>Illustrative Peer Benchmark — Synthetic Data Only</p>
              </div>
              <Btn small outline color="#475569" onClick={async () => {
                if (!benchmarks) {
                  const r = await dbnApi.peerBenchmarks();
                  setBenchmarks(r.data);
                }
                setShowBenchmarks(v => !v);
              }}>
                {showBenchmarks ? "Hide" : "Show Benchmarks"}
              </Btn>
            </div>
          </div>

          {showBenchmarks && benchmarks && (
            <>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:12 }}>
                {[
                  {l:"Above Peer Median",    v:benchmarks.summary.above_peer_median,    c:"#22c55e"},
                  {l:"At/Above Target",      v:benchmarks.summary.at_or_above_target,   c:"#34d399"},
                  {l:"Gaps to Close",        v:benchmarks.summary.gaps_to_close,        c:"#f59e0b"},
                  {l:"Illustrative Percentile",v:`${benchmarks.summary.overall_percentile}th`, c:"#60a5fa"},
                ].map(x => (
                  <div key={x.l} style={{ background:"#060f1e", border:"1px solid #0f2033",
                    borderTop:`3px solid ${x.c}`, borderRadius:10, padding:"10px 12px" }}>
                    <p style={{ fontSize:18, fontWeight:900, color:x.c }}>{x.v}</p>
                    <p style={{ fontSize:9, color:"#475569", textTransform:"uppercase", letterSpacing:"0.07em" }}>{x.l}</p>
                  </div>
                ))}
              </div>
              <div style={{ overflowX:"auto" }}>
                <div style={{ minWidth:900 }}>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart
                      data={(benchmarks.benchmarks||[]).map(b=>({
                        name:b.metric.length>20?b.metric.slice(0,18)+"…":b.metric,
                        fullName:b.metric, dbn:b.dbn,
                        peer_median:b.peer_median, top_quartile:b.top_quartile,
                        unit:b.unit, gap:b.gap,
                      }))}
                      margin={{ top:10, right:16, bottom:56, left:16 }}>
                      <CartesianGrid stroke="#0f2033" strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fill:"#475569", fontSize:8 }} angle={-35} textAnchor="end" interval={0} />
                      <YAxis tick={{ fill:"#475569", fontSize:8 }} />
                      <Tooltip
                        content={({active,payload,label})=>{
                          if(!active||!payload?.length) return null;
                          const d=payload[0]?.payload;
                          return (
                            <div style={{background:"#0a1628",border:"1px solid #1e3a5f",borderRadius:10,padding:"10px 14px",fontSize:10}}>
                              <p style={{fontWeight:700,color:"#f0f9ff",marginBottom:6}}>{d?.fullName}</p>
                              {payload.map(p=><p key={p.name} style={{color:p.color}}>{p.name}: {p.value} {d?.unit}</p>)}
                            </div>
                          );
                        }}
                      />
                      <Legend wrapperStyle={{fontSize:9,color:"#475569"}} />
                      <Bar dataKey="dbn" name="DBN" radius={[3,3,0,0]}>
                        {(benchmarks.benchmarks||[]).map((b,i)=>(
                          <Cell key={i} fill={b.gap===0?"#34d399":"#f59e0b"} />
                        ))}
                      </Bar>
                      <Bar dataKey="peer_median"   name="Peer Median"   fill="#334155" radius={[3,3,0,0]} />
                      <Bar dataKey="top_quartile"  name="Top Quartile"  fill="#1e3a5f" radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div style={{ marginTop:10 }}>
                <p style={{ fontSize:9, fontWeight:700, color:"#64748b", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.08em" }}>
                  Priority Actions from Benchmarking
                </p>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  {(benchmarks.benchmarks||[]).filter(b=>b.gap>0).slice(0,4).map(b=>(
                    <div key={b.metric} style={{ background:"#060f1e", borderRadius:8, padding:"8px 12px",
                      borderLeft:`3px solid ${b.gap>5?"#f97316":"#f59e0b"}` }}>
                      <p style={{ fontSize:10, fontWeight:700, color:"#f0f9ff", marginBottom:2 }}>{b.metric}</p>
                      <p style={{ fontSize:9, color:"#475569", marginBottom:3 }}>
                        DBN: {b.dbn}{b.unit} | Target: {b.target}{b.unit}
                      </p>
                      <p style={{ fontSize:8.5, color:"#64748b", lineHeight:1.4 }}>{b.action}</p>
                    </div>
                  ))}
                </div>
              </div>
              <p style={{ fontSize:8, color:"#1e3a5f", marginTop:10 }}>
                {benchmarks.disclaimer} {benchmarks.peer_group}
              </p>
            </>
          )}

          {!showBenchmarks && (
            <div style={{ display:"flex", gap:8, marginTop:10, flexWrap:"wrap" }}>
              {[
                {l:"Deployment Rate",      v:"36.2%", above:false},
                {l:"Mobilisation Ratio",   v:"2.6x",  above:true},
                {l:"Green Allocation",     v:"54%",   above:false},
                {l:"Jobs per EUR 1M",      v:"89",    above:true},
                {l:"Portfolio at Risk",    v:"2.1%",  above:true},
                {l:"Reporting Completeness",v:"83%",  above:false},
              ].map(x => (
                <div key={x.l} style={{ background:"#060f1e", borderRadius:7, padding:"7px 12px",
                  borderTop:`2px solid ${x.above?"#34d399":"#f59e0b"}`, minWidth:100, textAlign:"center" }}>
                  <p style={{ fontSize:14, fontWeight:900, color:x.above?"#34d399":"#f59e0b" }}>{x.v}</p>
                  <p style={{ fontSize:8, color:"#475569" }}>{x.l}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
